import axiosClient from "utils/axios";
import { commaSeparatedEnums, positiveInt } from "utils/list-query";
import { ApiList, ApiOptionList, ApiResponse } from "../../../types/api.types";
import { PettyWorkStatus } from "features/PettyWorks/constants/petty-work.constants";
import {
  AddPettyWorkRequest,
  PettyWork,
  PettyWorkAssignmentSummary,
  UpdatePettyWorkRequest,
} from "features/PettyWorks/types/petty-work.types";
import { Employee } from "features/Employees/types/employee.types";

const PETTY_WORK_LIST_STATUSES = Object.values(PettyWorkStatus);

function normalizePettyWorkListRow(row: PettyWork): PettyWork {
  const r = row as unknown as Record<string, unknown>;
  const raw = r.assignments ?? r.workAssignments ?? r.work_assignments;
  const arr = Array.isArray(raw) ? raw : [];
  if (!arr.length) return row;

  const assignments: PettyWorkAssignmentSummary[] = arr.map((item) => {
    const a = item;
    const u = a.user;
    return {
      id: a.id,
      toUserId: a.toUserId,
      status: a.status ?? null,
      isActive: a.isActive,
      actualEndDate: a.actualEndDate ?? null,
      deleted_at: a.deleted_at ?? null,
      user: u
        ? {
            id: u.id,
            firstName: u.firstName,
            lastName: u.lastName,
            email: u.email,
            avatar: u.avatar,
            employee: u.employee as Employee,
          }
        : null,
    };
  });

  return { ...row, assignments };
}

export type GetPettyWorksListParams = {
  page?: number;
  limit?: number;
  search?: string;
  status?: string | string[];
  clientId?: number | string;
  priorityId?: number | string;
  sourceChannelId?: number | string;
};

export const getPettyWorkOptions = async ({
  query,
  limit,
  page,
  status,
}: {
  query?: string;
  limit?: number;
  page?: number;
  status?: string;
} = {}) => {
  try {
    const response = await axiosClient.get<ApiResponse<ApiOptionList>>(
      `/petty-works/options`,
      {
        params: {
          query,
          limit,
          page,
          status: status || undefined,
        },
      },
    );
    return response.data;
  } catch (err) {
    console.warn("Error fetching Petty Work options:", err);
    throw err;
  }
};

export const getPettyWorks = async ({
  page,
  limit,
  search,
  status,
  clientId,
  priorityId,
  sourceChannelId,
}: GetPettyWorksListParams = {}) => {
  try {
    const response = await axiosClient.get<ApiResponse<ApiList<PettyWork[]>>>(
      "/petty-works",
      {
        params: {
          page,
          limit,
          search: search?.trim() || undefined,
          status: commaSeparatedEnums(status, PETTY_WORK_LIST_STATUSES),
          clientId: positiveInt(clientId),
          priorityId: positiveInt(priorityId),
          sourceChannelId: positiveInt(sourceChannelId),
        },
      },
    );
    const body = response.data;
    if (body.success && body.data && Array.isArray(body.data.rows)) {
      return {
        ...body,
        data: {
          ...body.data,
          rows: body.data.rows.map((row) => normalizePettyWorkListRow(row)),
        },
      };
    }
    return body;
  } catch (err) {
    console.warn("Error fetching petty-works : ", err);
    throw err;
  }
};

export const createPettyWork = async (payload: AddPettyWorkRequest) => {
  try {
    const response = await axiosClient.post<ApiResponse<PettyWork>>(
      "/petty-works",
      payload,
    );
    return response.data;
  } catch (err) {
    console.warn("Error creating Petty Work : ", err);
    throw err;
  }
};

export const updatePettyWork = async (
  id: number,
  payload: UpdatePettyWorkRequest,
) => {
  try {
    const response = await axiosClient.put<ApiResponse<PettyWork>>(
      `/petty-works/${id}`,
      payload,
    );
    return response.data;
  } catch (err) {
    console.warn("Error updating Petty Work : ", err);
    throw err;
  }
};


export const getPettyWork = async (id: number) => {
  try {
    if (!Number.isInteger(id) || id <= 0) {
      throw new Error("Invalid ID");
    }
    const response = await axiosClient.get<ApiResponse<PettyWork>>(
      `/petty-works/${id}`,
    );
    const body = response.data;
    if (body.success && body.data) {
      return { ...body, data: normalizePettyWorkListRow(body.data) };
    }
    return body;
  } catch (err) {
    console.warn("Error fetching Petty Work : ", err);
    throw err;
  }
};

export const deletePettyWork = async (id: number) => {
  try {
    const response = await axiosClient.delete<ApiResponse<PettyWork>>(
      `/petty-works/${id}`,
    );
    return response.data;
  } catch (err) {
    console.warn("Error deleting Petty Work : ", err);
    throw err;
  }
};

export const cancelPettyWork = async (
  id: number,
  payload?: { reason?: string | null },
) => {
  try {
    const response = await axiosClient.post<ApiResponse<PettyWork>>(
      `/petty-works/${id}/cancel`,
      payload ?? {},
    );
    return response.data;
  } catch (err) {
    console.warn("Error cancelling Petty Work : ", err);
    throw err;
  }
};

/** APPROVED → IN_PROGRESS (requires linked work order; idempotent if already in progress). */
export const startPettyWork = async (id: number) => {
  try {
    const response = await axiosClient.post<ApiResponse<PettyWork>>(
      `/petty-works/${id}/start`,
      {},
    );
    return response.data;
  } catch (err) {
    console.warn("Error starting Petty Work : ", err);
    throw err;
  }
};

/** IN_PROGRESS → ON_HOLD */
export const holdPettyWork = async (
  id: number,
  payload?: { reason?: string | null },
) => {
  try {
    const response = await axiosClient.post<ApiResponse<PettyWork>>(
      `/petty-works/${id}/hold`,
      payload ?? {},
    );
    return response.data;
  } catch (err) {
    console.warn("Error holding Petty Work : ", err);
    throw err;
  }
};

/** ON_HOLD → IN_PROGRESS */
export const resumePettyWork = async (id: number) => {
  try {
    const response = await axiosClient.post<ApiResponse<PettyWork>>(
      `/petty-works/${id}/resume`,
      {},
    );
    return response.data;
  } catch (err) {
    console.warn("Error resuming Petty Work : ", err);
    throw err;
  }
};

/**
 * IN_PROGRESS → PENDING_COMPLETION_APPROVAL.
 * Multipart: `feedback` (string) + one or more `completionDocuments` files.
 */
export const submitPettyWorkCompletion = async (id: number, data: FormData) => {
  try {
    const response = await axiosClient.post<ApiResponse<PettyWork>>(
      `/petty-works/${id}/submit-completion`,
      data,
      { headers: { "Content-Type": "multipart/form-data" } },
    );
    return response.data;
  } catch (err) {
    console.warn("Error submitting petty work completion : ", err);
    throw err;
  }
};

/** PENDING_COMPLETION_APPROVAL → READY_FOR_BILLING. Gated by petty_work.approve. */
export const approvePettyWorkForBilling = async (
  id: number,
  payload?: { notes?: string | null },
) => {
  try {
    const response = await axiosClient.post<ApiResponse<PettyWork>>(
      `/petty-works/${id}/approve-for-billing`,
      payload ?? {},
    );
    return response.data;
  } catch (err) {
    console.warn("Error approving petty work for billing : ", err);
    throw err;
  }
};

/** PENDING_COMPLETION_APPROVAL → IN_PROGRESS. Requires a reason. Gated by petty_work.reject. */
export const rejectPettyWorkCompletion = async (
  id: number,
  payload: { reason: string },
) => {
  try {
    const response = await axiosClient.post<ApiResponse<PettyWork>>(
      `/petty-works/${id}/reject-completion`,
      payload,
    );
    return response.data;
  } catch (err) {
    console.warn("Error rejecting petty work completion : ", err);
    throw err;
  }
};

/**
 * READY_FOR_BILLING → COMPLETED. Gated by petty_work.submit_billing.
 * Generates the invoice from the BOQ items and settles any linked client advance.
 * `finalAmount`, if given, overrides the agreed amount — the invoice adds an
 * adjustment line when it differs from the items total.
 */
export const submitPettyWorkBilling = async (
  id: number,
  payload: { finalAmount?: number | null } = {},
) => {
  try {
    const response = await axiosClient.post<ApiResponse<PettyWork>>(
      `/petty-works/${id}/submit-billing`,
      payload,
    );
    return response.data;
  } catch (err) {
    console.warn("Error submitting petty work billing : ", err);
    throw err;
  }
};
